import { ModalCloseButton } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { QualityPicker } from './PostCutSummary';

// Rating step — runs RIGHT AFTER endCut, before the after-photo capture.
//
// Why here and not the post-cut summary:
//   - Barber's judgment is freshest right after the cut. Three minutes
//     into capturing photos, signing off the price, picking payment
//     method, etc., the "was that a good cut?" signal has dulled.
//   - Capture rate matters more than perfect timing. The same logic as
//     moving walk-in/appointment to check-in: catch the answer when
//     attention is highest.
//
// UX rule: tap-to-rate IS tap-to-advance. There's no separate Continue
// or Skip button — that would punish the answer we want (two taps to
// rate, one tap to skip). Picking a face commits + advances in one
// tap. The X close still minimizes if the barber genuinely needs out,
// but escaping is now SLOWER than answering. PostCutSummary's picker
// still surfaces as a final correction step, seeded from pendingQuality.

export function RatingStep() {
  const t = useT();
  const pendingQuality = useCutTimer((s) => s.pendingQuality);
  const setPendingQuality = useCutTimer((s) => s.setPendingQuality);
  const goToAfterPhoto = useCutTimer((s) => s.goToAfterPhotoFromRating);
  const closeModal = useCutTimer((s) => s.closeModal);

  // Pick a rating → set value, then advance. The advance fires only on
  // a non-null pick so the existing tap-same-chip-to-clear toggle in
  // QualityPicker doesn't accidentally trigger a forward step on
  // deselect (rare on this screen — the barber gets one tap then this
  // unmounts — but the guard keeps semantics clean).
  function handlePick(q: 1 | 2 | 3 | null) {
    setPendingQuality(q);
    if (q !== null) goToAfterPhoto();
  }

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mt-2 mb-2">
          <CheckIcon className="w-3 h-3" />
          {t('cut.rating.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.rating.title')}</h2>
        <p className="text-white/72 text-sm leading-relaxed mt-2 max-w-sm">
          {t('cut.rating.body')}
        </p>
      </div>

      <div className="p-5 space-y-4">
        <QualityPicker value={pendingQuality} onChange={handlePick} />

        {/* Tap-to-advance hint — micro-copy explaining the one-tap
            interaction. Soft text, not a button: the chips above ARE
            the buttons. */}
        <p className="text-[11px] text-ink-500 text-center leading-snug">
          {t('cut.rating.tapHint')}
        </p>
      </div>
    </>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
